
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { UserProfile } from '@/types/auth';

interface UseAuthActionsProps {
  setUser: React.Dispatch<React.SetStateAction<any>>;
  setSession: React.Dispatch<React.SetStateAction<any>>;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  loadUserProfile: (userId: string) => Promise<UserProfile | null>;
}

export const useAuthActions = ({
  setUser,
  setSession,
  setUserProfile,
  setLoading,
  loadUserProfile
}: UseAuthActionsProps) => {
  const navigate = useNavigate();

  // Fonction de connexion
  const signIn = useCallback(async (email: string, password: string, returnTo?: string) => {
    try {
      setLoading(true);
      console.log("Tentative de connexion pour:", email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error("Erreur de connexion:", error);
        toast.error("Échec de la connexion", {
          description: error.message || "Veuillez vérifier vos identifiants"
        });
        throw error;
      }
      
      console.log("Connexion réussie, session créée");
      setSession(data.session);
      setUser(data.session.user);
      
      // Charger le profil de l'utilisateur
      await loadUserProfile(data.session.user.id);
      
      // Rediriger l'utilisateur
      const redirectPath = returnTo || '/index';
      navigate(redirectPath, { replace: true });
      
      toast.success("Connexion réussie", {
        description: "Bienvenue dans votre espace"
      });
    } catch (err) {
      console.error("Exception dans signIn:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [navigate, loadUserProfile, setLoading, setSession, setUser]);

  // Fonction de déconnexion
  const signOut = useCallback(async () => {
    try {
      setLoading(true);
      console.log("Déconnexion en cours...");
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Erreur lors de la déconnexion:', error);
        toast.error("Erreur lors de la déconnexion");
        throw error;
      }
      
      // Nettoyer les états
      setSession(null);
      setUser(null);
      setUserProfile(null);
      
      navigate('/login', { replace: true });
      toast.success("Déconnexion réussie");
    } catch (error) {
      console.error('Exception dans signOut:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [navigate, setLoading, setSession, setUser, setUserProfile]);

  // Fonction pour rafraîchir manuellement la session
  const refreshSession = useCallback(async () => {
    try {
      setLoading(true);
      console.log("Rafraîchissement de la session...");
      
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error("Erreur lors du rafraîchissement de la session:", error);
        throw error;
      }
      
      if (data.session) {
        console.log("Session récupérée avec succès");
        setSession(data.session);
        setUser(data.session.user);
        await loadUserProfile(data.session.user.id);
      } else {
        console.log("Aucune session active trouvée");
        setSession(null);
        setUser(null);
        setUserProfile(null);
      }
    } catch (err) {
      console.error("Exception dans refreshSession:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadUserProfile, setLoading, setSession, setUser, setUserProfile]);

  return { signIn, signOut, refreshSession };
};
